/**
 * Per-specialty copy for the two audiences.
 *
 *  - `patientIntro` + `commonReasons`: plain-language "what does this kind of
 *    specialist do" for patients and carers (the default view, and what the
 *    prerender ships to search engines).
 *  - `gpNotes`: short, general "include in the referral" prompts for GP mode.
 *
 * Rules (AHPRA s133 / CLAUDE.md rule 6): general information only — no
 * outcome claims, no "best", no testimonials, no treatment or dosing advice.
 * `reviewedBy` stays null until a clinician has read the entry; the
 * prerender logs unreviewed entries on every build.
 *
 * Keyed by the API specialty slug (lib/specialty-slug.ts).
 */
export interface SpecialtyContent {
  /** "cardiologist" — used in titles and sentences. */
  practitioner: string;
  /** "cardiologists" */
  practitionerPlural: string;
  patientIntro: string;
  commonReasons: string[];
  gpNotes: string[];
  /** Official pages the patient copy is checked against (scripts/review-copy.mjs).
   *  Empty = no official source found yet, so the page stays out of Google. */
  sources: string[];
  reviewedBy: string | null;
}

export const SPECIALTY_CONTENT: Record<string, SpecialtyContent> = {
  cardiology: {
    practitioner: "cardiologist",
    practitionerPlural: "cardiologists",
    patientIntro:
      "Cardiologists are doctors who specialise in the heart and blood vessels. Your GP may refer you to a cardiologist to investigate symptoms, run heart tests such as an echocardiogram or stress test, or help manage an ongoing heart condition.",
    commonReasons: ["Chest pain or palpitations", "High blood pressure", "Heart rhythm problems", "Heart failure", "Heart murmurs and valve problems"],
    gpNotes: ["Reason for referral and urgency", "Recent ECG", "Relevant bloods (e.g. lipids, renal function)", "Current medications and cardiac history"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/the-role-of-a-cardiologist", "https://www.healthdirect.gov.au/conditions/heart-diseases/heart-valve-disease", "https://www.healthdirect.gov.au/symptoms/heart-murmur"],
    reviewedBy: null,
  },
  dermatology: {
    practitioner: "dermatologist",
    practitionerPlural: "dermatologists",
    patientIntro:
      "Dermatologists are doctors who specialise in the skin, hair and nails. GPs refer to a dermatologist for skin cancer checks and treatment, and for skin conditions that need specialist assessment.",
    commonReasons: ["Suspicious moles or skin cancer", "Eczema and dermatitis", "Psoriasis", "Acne", "Hair and nail conditions"],
    gpNotes: ["Reason for referral and urgency", "Photos of the lesion if available", "Relevant histology", "Treatments already tried"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/dermatologist"],
    reviewedBy: null,
  },
  endocrinology: {
    practitioner: "endocrinologist",
    practitionerPlural: "endocrinologists",
    patientIntro:
      "Endocrinologists are doctors who specialise in hormones and the glands that make them. Your GP may refer you to an endocrinologist for diabetes, thyroid conditions or other hormone problems.",
    commonReasons: ["Diabetes", "Thyroid conditions", "Osteoporosis and bone health", "Pituitary and adrenal conditions"],
    gpNotes: ["Reason for referral", "Relevant results (e.g. HbA1c, thyroid function)", "Current medications", "Relevant imaging"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/endocrinologist"],
    reviewedBy: null,
  },
  "ent-ear-nose-and-throat": {
    practitioner: "ENT surgeon",
    practitionerPlural: "ENT surgeons",
    patientIntro:
      "ENT (ear, nose and throat) surgeons specialise in conditions of the ears, nose, sinuses, throat, head and neck. GPs refer to an ENT surgeon for hearing and balance problems, sinus and tonsil issues, and lumps in the head and neck.",
    commonReasons: ["Hearing loss and ear infections", "Sinus problems", "Tonsil and adenoid problems", "Snoring and sleep apnoea", "Lumps in the head or neck"],
    gpNotes: ["Reason for referral and urgency", "Audiology results if available", "Relevant imaging", "Treatments already tried"],
    sources: ["https://www.betterhealth.vic.gov.au/health/serviceprofiles/Ear-nose-and-throat-specialist-services", "https://www.healthdirect.gov.au/conditions/sleep-disorders/obstructive-sleep-apnoea", "https://www.healthdirect.gov.au/conditions/sleep-disorders/how-to-stop-snoring"],
    reviewedBy: null,
  },
  "gastroenterology-and-hepatology": {
    practitioner: "gastroenterologist",
    practitionerPlural: "gastroenterologists",
    patientIntro:
      "Gastroenterologists specialise in the digestive system, including the liver. Your GP may refer you for a colonoscopy or gastroscopy, or for help with an ongoing digestive or liver condition.",
    commonReasons: ["Reflux and swallowing problems", "Inflammatory bowel disease (Crohn's, colitis)", "Liver conditions and abnormal liver tests"],
    gpNotes: ["Reason for referral and urgency", "Screening (FOBT) result if relevant", "Relevant bloods (e.g. FBC, iron studies, LFTs)", "Current medications, including anticoagulants"],
    sources: ["https://www.betterhealth.vic.gov.au/health/serviceprofiles/Gastroenterologist-services", "https://www.healthdirect.gov.au/procedures/health-screenings/bowel-cancer-screening", "https://www.healthdirect.gov.au/conditions/liver-and-pancreas-diseases/fatty-liver", "https://www.healthdirect.gov.au/conditions/liver-and-pancreas-diseases/hepatitis"],
    reviewedBy: null,
  },
  "geriatrics-general-medicine": {
    practitioner: "geriatrician",
    practitionerPlural: "geriatricians and general physicians",
    patientIntro:
      "Geriatricians specialise in the health of older people. Your GP may refer you for a comprehensive assessment, memory concerns, falls or a review of many medicines.",
    commonReasons: ["Memory and thinking concerns", "Falls", "Several ongoing conditions at once", "Reviewing many medicines", "Planning care and support at home"],
    gpNotes: ["Reason for referral", "Current medication list", "Cognitive screening results if done", "Carer and living situation"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/geriatrician", "https://www.healthdirect.gov.au/accidents-and-injuries/falls"],
    reviewedBy: null,
  },
  haematology: {
    practitioner: "haematologist",
    practitionerPlural: "haematologists",
    patientIntro:
      "Haematologists specialise in blood and blood-forming tissues. Your GP may refer you to a haematologist for abnormal blood tests, bleeding or clotting problems, or blood cancers such as lymphoma.",
    commonReasons: ["Abnormal blood counts", "Anaemia and iron problems", "Bleeding or clotting disorders", "Blood cancers (e.g. lymphoma, leukaemia)"],
    gpNotes: ["Reason for referral and urgency", "Recent FBC and film", "Relevant iron studies or coagulation results", "Current medications"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/haematologist"],
    reviewedBy: null,
  },
  "infectious-diseases": {
    practitioner: "infectious diseases physician",
    practitionerPlural: "infectious diseases physicians",
    patientIntro:
      "Infectious diseases physicians specialise in complex, unusual or ongoing infections. Your GP may refer you after a difficult infection, for travel-related illness, or for long-term conditions such as HIV or hepatitis.",
    commonReasons: ["Infections that keep coming back", "Travel-related illness", "HIV and viral hepatitis", "Infections that haven't responded to treatment"],
    gpNotes: ["Reason for referral and urgency", "Relevant microbiology and serology", "Antibiotics already tried", "Travel and exposure history"],
    sources: [],
    reviewedBy: null,
  },
  "medical-oncology": {
    practitioner: "medical oncologist",
    practitionerPlural: "medical oncologists",
    patientIntro:
      "Medical oncologists use medicines to treat cancer, such as chemotherapy, hormone therapy and immunotherapy. Medical, surgical and radiation oncologists often work together as a team to diagnose, treat and care for people with cancer.",
    commonReasons: ["A new cancer diagnosis", "Treatment planning after cancer surgery", "Ongoing cancer care and follow-up"],
    gpNotes: ["Diagnosis and histology", "Staging imaging", "Treating surgeon or team", "Urgency"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/oncologist", "https://www.healthdirect.gov.au/treatments-and-self-care/cancer-treatments"],
    reviewedBy: null,
  },
  nephrology: {
    practitioner: "nephrologist",
    practitionerPlural: "nephrologists (kidney specialists)",
    patientIntro:
      "Nephrologists are kidney specialists. Your GP may refer you to a nephrologist for reduced kidney function, protein or blood in the urine, or kidney-related blood pressure problems.",
    commonReasons: ["Chronic kidney disease", "Protein or blood in the urine", "Kidney-related high blood pressure"],
    gpNotes: ["Reason for referral", "eGFR trend and urine ACR", "Renal imaging if done", "Current medications"],
    sources: ["https://www.betterhealth.vic.gov.au/health/conditionsandtreatments/kidney-disease", "https://www.healthdirect.gov.au/conditions/kidney-and-urinary-problems/chronic-kidney-disease"],
    reviewedBy: null,
  },
  neurology: {
    practitioner: "neurologist",
    practitionerPlural: "neurologists",
    patientIntro:
      "Neurologists specialise in the brain, spinal cord and nerves. Your GP may refer you to a neurologist for headaches, seizures, numbness or weakness, or conditions such as Parkinson's disease and multiple sclerosis.",
    commonReasons: ["Headaches and migraine", "Seizures and epilepsy", "Numbness, tingling or weakness", "Movement disorders (e.g. Parkinson's)", "Multiple sclerosis"],
    gpNotes: ["Reason for referral and urgency", "Relevant imaging", "Nerve studies if done", "Current medications"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/neurologist"],
    reviewedBy: null,
  },
  neurosurgery: {
    practitioner: "neurosurgeon",
    practitionerPlural: "neurosurgeons",
    patientIntro:
      "Neurosurgeons are surgeons who operate on the brain, spine and nerves. Your GP may refer you to a neurosurgeon for some spine conditions, nerve compression or a brain condition found on a scan.",
    commonReasons: ["Spine conditions with nerve compression", "Brain tumours and lesions", "Some types of neck and back pain"],
    gpNotes: ["Reason for referral and urgency", "Recent MRI or CT report", "Neurological findings", "Treatments already tried"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/neurosurgeon"],
    reviewedBy: null,
  },
  "orthopaedic-surgery": {
    practitioner: "orthopaedic surgeon",
    practitionerPlural: "orthopaedic surgeons",
    patientIntro:
      "Orthopaedic surgeons specialise in bones, joints, ligaments and tendons. GPs refer to an orthopaedic surgeon for joint problems, sports injuries, fractures and conditions that may need surgery.",
    commonReasons: ["Knee, hip and shoulder problems", "Sports injuries", "Fractures", "Spine conditions"],
    gpNotes: ["Reason for referral and body region", "Recent X-ray, ultrasound or MRI reports", "Treatments already tried (e.g. physiotherapy)", "Urgency"],
    sources: ["https://www.betterhealth.vic.gov.au/health/serviceprofiles/Orthopaedic-Surgeon"],
    reviewedBy: null,
  },
  "pain-medicine": {
    practitioner: "pain medicine specialist",
    practitionerPlural: "pain medicine specialists",
    patientIntro:
      "If you have chronic pain, your doctor may refer you to a pain management program at a pain clinic. Many people find support from a few different types of health professionals helpful.",
    commonReasons: ["Chronic back or neck pain", "Nerve pain", "Pain after surgery or injury"],
    gpNotes: ["Reason for referral and pain history", "Relevant imaging", "Treatments and medicines already tried", "Other services involved"],
    sources: ["https://www.betterhealth.vic.gov.au/health/conditionsandtreatments/pain-and-pain-management-adults", "https://www.healthdirect.gov.au/healthcare-and-services/general-health/options-for-managing-chronic-pain", "https://www.healthdirect.gov.au/symptoms/aches-and-pains/chronic-pain", "https://www.healthdirect.gov.au/symptoms/aches-and-pains/nerve-pain", "https://www.healthdirect.gov.au/symptoms/aches-and-pains/neck-pain"],
    reviewedBy: null,
  },
  "plastic-and-reconstructive-surgery": {
    practitioner: "plastic surgeon",
    practitionerPlural: "plastic and reconstructive surgeons",
    patientIntro:
      "A specialist plastic surgeon is a doctor who specialises in reconstructive and cosmetic surgery.",
    commonReasons: ["Reconstructive surgery"],
    gpNotes: ["Reason for referral and urgency", "Histology if available", "Photos if appropriate", "Relevant history"],
    sources: ["https://www.betterhealth.vic.gov.au/health/serviceprofiles/specialist-plastic-surgeon-service"],
    reviewedBy: null,
  },
  psychiatry: {
    practitioner: "psychiatrist",
    practitionerPlural: "psychiatrists",
    patientIntro:
      "Psychiatrists are medical doctors who specialise in mental health. If you need to see a psychiatrist, your GP can give you a referral. Psychiatrists diagnose illness and manage treatment that may include medicines.",
    commonReasons: ["Depression and anxiety", "Bipolar disorder", "ADHD assessment", "Psychosis"],
    gpNotes: ["Reason for referral and urgency", "Mental health care plan if prepared", "Risk assessment", "Current medications"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/psychiatrists-and-psychologists", "https://www.healthdirect.gov.au/conditions/disability-and-developmental-disorders/attention-deficit-disorder-add-or-adhd", "https://www.healthdirect.gov.au/conditions/mental-health-conditions/psychosis"],
    reviewedBy: null,
  },
  rheumatology: {
    practitioner: "rheumatologist",
    practitionerPlural: "rheumatologists",
    patientIntro:
      "Rheumatologists specialise in arthritis and autoimmune conditions affecting the joints, muscles and other parts of the body. Your GP may refer you for joint swelling, gout or conditions such as lupus.",
    commonReasons: ["Inflammatory arthritis (e.g. rheumatoid arthritis)", "Gout", "Lupus and connective tissue diseases", "Osteoporosis", "Vasculitis"],
    gpNotes: ["Reason for referral", "Relevant bloods (e.g. CRP, ESR, autoantibodies)", "Joint imaging if done", "Current medications"],
    sources: ["https://www.betterhealth.vic.gov.au/health/serviceprofiles/Rheumatologist", "https://www.healthdirect.gov.au/conditions/vascular-diseases/vasculitis"],
    reviewedBy: null,
  },
  urology: {
    practitioner: "urologist",
    practitionerPlural: "urologists",
    patientIntro:
      "Urologists are surgeons who specialise in the kidneys, bladder and prostate, and the male reproductive system. GPs refer to a urologist for kidney stones, blood in the urine, prostate concerns and bladder problems.",
    commonReasons: ["Raised PSA or prostate concerns", "Blood in the urine", "Kidney stones", "Bladder and urinary problems"],
    gpNotes: ["Reason for referral and urgency", "PSA history if relevant", "Urine and renal results", "Relevant imaging"],
    sources: ["https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/urologist", "https://www.healthdirect.gov.au/procedures/diagnostic-tests-and-scans/blood-tests/prostate-specific-antigen-PSA-test"],
    reviewedBy: null,
  },
  "vascular-surgery": {
    practitioner: "vascular surgeon",
    practitionerPlural: "vascular surgeons",
    patientIntro:
      "Vascular surgeons treat problems with the blood vessels. GPs may refer to a vascular surgeon for poor circulation in the legs, aneurysms and vein conditions such as varicose veins.",
    commonReasons: ["Leg pain when walking (poor circulation)", "Aortic aneurysm", "Varicose veins"],
    gpNotes: ["Reason for referral and urgency", "Relevant duplex or imaging reports", "Vascular risk factors", "Current medications"],
    sources: ["https://www.betterhealth.vic.gov.au/health/conditionsandtreatments/peripheral-vascular-disease", "https://www.healthdirect.gov.au/conditions/heart-diseases/abdominal-aortic-aneurysm", "https://www.healthdirect.gov.au/conditions/vascular-diseases/varicose-veins", "https://www.healthdirect.gov.au/conditions/heart-diseases/aneurysms", "https://www.healthdirect.gov.au/conditions/vascular-diseases"],
    reviewedBy: null,
  },
};

export function specialtyContent(slug: string): SpecialtyContent | null {
  return SPECIALTY_CONTENT[slug] ?? null;
}
